/**
 * @fileoverview Conditional field visibility (MOR-885).
 *
 * A template can mark a field as DEPENDENT on another field's answer: the
 * dependent field only surfaces in the generate-precedents review form and
 * only fills into the generated document when the controlling field's value
 * matches one of the configured values. This powers "smart groupings" like
 * a trust ABN that should only flow through when "Is, or was, the entity
 * the trustee of a trust?" is answered "Yes" — with No/Unsure (or no
 * answer) the ABN is suppressed instead of uselessly filling the form.
 *
 * Conditions are stored on `precedentTemplates.fieldConditions`, keyed by
 * the dependent field key. The controlling key can be a canonical field, a
 * custom field, or a checkbox-group key (the synthetic single-select the
 * wizard builds from grouped checkbox placements).
 *
 * Chained conditions are supported: when A gates B and B gates C, hiding B
 * also hides C (a suppressed field's value is treated as unanswered when
 * evaluating downstream conditions).
 *
 * Dependency-free by design (like contactFields.ts) — imported by both the
 * Convex backend (PDF fill) and the frontend wizard.
 */

export interface FieldCondition {
  /** Controlling field key whose value gates the dependent field. */
  fieldKey: string;
  /** Values (any match, trimmed + case-insensitive) that enable the field. */
  values: string[];
}

export type FieldConditionMap = Record<string, FieldCondition>;

/** Trim + lowercase for comparison; empty → null (unanswered). */
function normalize(value: string | undefined | null): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Whether one condition is met by the given field values. An unanswered
 * controlling field never matches — dependent fields stay suppressed until
 * the controlling question is explicitly answered with a matching value.
 */
export function isFieldConditionMet(
  condition: FieldCondition,
  fieldValues: Record<string, string | undefined>,
): boolean {
  const actual = normalize(fieldValues[condition.fieldKey]);
  if (actual === null) return false;
  return condition.values.some((v) => normalize(v) === actual);
}

/**
 * Resolve the full set of SUPPRESSED dependent field keys for a document.
 * Runs to a fixpoint so chained conditions cascade: once a field is
 * suppressed, its value no longer satisfies conditions that depend on it.
 */
export function resolveSuppressedFieldKeys(
  conditions: FieldConditionMap | undefined | null,
  fieldValues: Record<string, string | undefined>,
): Set<string> {
  const suppressed = new Set<string>();
  if (!conditions) return suppressed;

  const entries = Object.entries(conditions);
  // Each pass can only grow the suppressed set, so at most `entries.length`
  // passes are needed before the fixpoint.
  for (let pass = 0; pass < entries.length; pass++) {
    const sizeBefore = suppressed.size;
    for (const [dependentKey, condition] of entries) {
      if (suppressed.has(dependentKey)) continue;
      const met =
        !suppressed.has(condition.fieldKey) &&
        isFieldConditionMet(condition, fieldValues);
      if (!met) {
        suppressed.add(dependentKey);
      }
    }
    if (suppressed.size === sizeBefore) break;
  }
  return suppressed;
}
